import "./App.css";
import { useCallback } from "react";
import { jsPDF } from "jspdf";
import useImageState from "./useImageState";
import CustomForm from "./CustomForm";
import flipImageHorizontally from "./imageFlipping";

const App = () => {
  const { setError, setIsGenerating } = useImageState();

  // Function to handle file input change
  const generatePDF = async (data) => {
    setIsGenerating(true);
    setError(null);

    // 1. Input Validation and Preparation
    try {
      // Convert all millimeter strings to numbers
      const userImage =
        data.image_flip === "Yes"
          ? await flipImageHorizontally(data.image_data_url)
          : data.image_data_url;
      const imgW = parseFloat(data.image_width);
      const imgH =
        data.print_type === "Free" ? parseFloat(data.image_height) : imgW;
      const topSpace = parseFloat(data.top_space || 0);
      const leftSpace = parseFloat(data.left_space || 0);
      const colGap = parseFloat(data.column_gap || 0);
      const rowGap = parseFloat(data.row_gap || 0);

      // Basic input checks
      if (!data.image_data_url || !imgW || !imgH) {
        throw new Error(
          "Please upload an image and specify both width and height."
        );
      }

      // 2. Initialize PDF Document
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm", // Key: Use millimeters for user-specified dimensions
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Determine image type for jsPDF
      const fileTypeMatch = data.image_data_url.match(
        /^data:image\/([a-zA-Z]+);/
      );
      const fileType = fileTypeMatch ? fileTypeMatch[1].toUpperCase() : "JPEG";

      // Calculate available print area boundaries
      const maxPrintX = pageWidth;
      const maxPrintY = pageHeight;

      // 3. Tiling Variables Setup
      // Start positions
      let x = leftSpace;
      let y = topSpace;

      const ctx = doc.context2d; // jsPDF canvas context
      const radius = imgW / 2; // Circle diameter = width

      while (true) {
        // Stop if image won't fit vertically
        if (y + imgH > maxPrintY) break;

        // Check if next image on the row would overflow horizontally
        if (x + imgW > maxPrintX) {
          // Move to next row
          x = leftSpace;
          y += imgH + rowGap;

          // Check again after moving rows
          if (y + imgH > maxPrintY) break;
        }

        if (data.print_type === "Round") {
          // Load original image at FULL resolution
          const img = new Image();
          img.src = userImage;

          await new Promise((resolve) => (img.onload = resolve));

          // Use original pixel size for canvas = perfect quality
          const pixelW = img.width;
          const pixelH = img.height;

          // Make a square canvas based on the smaller dimension
          const size = Math.min(pixelW, pixelH);

          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");

          // Clip to a perfect circle
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();

          // Center-crop image into the circle at full resolution
          const offsetX = (size - pixelW) / 2;
          const offsetY = (size - pixelH) / 2;

          ctx.drawImage(img, offsetX, offsetY);

          // Export HIGH resolution PNG
          const maskedImg = canvas.toDataURL("image/png", 1.0);

          // Add full-quality clipped image into PDF
          doc.addImage(maskedImg, "PNG", x, y, imgW, imgW);
        } else {
          // Place image
          doc.addImage(userImage, fileType, x, y, imgW, imgH, null, "FAST");
        }

        // Move right
        x += imgW + colGap;
      }

      // 5. Finalize and Save
      const fileName = data.image_name
        ? data.image_name.split(".")[0] + "_sheet.pdf"
        : "image_document.pdf";

      doc.save(fileName);
    } catch (e) {
      console.error("PDF Generation Error:", e);
      setError(e.message || "An error occurred during PDF generation.");
    } finally {
      setIsGenerating(false);
    }
  };

  return <CustomForm onGenerate={generatePDF} />;
};

export default App;
